export interface ShareCardData {
  patientName: string
  bloodType: string
  hospital: string
  city: string
  state: string
  urgency: string
  unitsNeeded: number
  patientPhoto?: string
}

export async function generateShareCard(data: ShareCardData): Promise<string> {
  console.log('🎨 Starting card generation with data:', data)
  
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    console.error('❌ Canvas not supported')
    throw new Error('Canvas not supported')
  }

  // Instagram Reels size (9:16 ratio)
  canvas.width = 1080
  canvas.height = 1920
  console.log('✅ Canvas created:', canvas.width, 'x', canvas.height)

  // Premium gradient background (Apple-style)
  const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height)
  gradient.addColorStop(0, '#FAFAFA')
  gradient.addColorStop(1, '#FFFFFF')
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // Top minimalist header with DOE branding
  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, canvas.width, 200)
  
  // Subtle top shadow
  const headerGradient = ctx.createLinearGradient(0, 200, 0, 240)
  headerGradient.addColorStop(0, 'rgba(0, 0, 0, 0.05)')
  headerGradient.addColorStop(1, 'rgba(0, 0, 0, 0)')
  ctx.fillStyle = headerGradient
  ctx.fillRect(0, 200, canvas.width, 40)

  // "DOE" logo - premium typography
  ctx.fillStyle = '#DC2626'
  ctx.font = 'bold 90px -apple-system, SF Pro Display, Arial'
  ctx.textAlign = 'center'
  ctx.letterSpacing = '2px'
  ctx.fillText('DOE', 540, 130)

  // Footer with download CTA - Apple style
  ctx.fillStyle = '#000000'
  ctx.font = '600 32px -apple-system, SF Pro Display, Arial'
  ctx.textAlign = 'center'
  ctx.fillText('Baixe o app DOE', 540, canvas.height - 100)
  
  ctx.fillStyle = '#6B7280'
  ctx.font = '400 26px -apple-system, SF Pro Display, Arial'
  ctx.fillText('doe.lumi.new', 540, canvas.height - 55)

  let currentY = 300

  // Patient photo section (if provided) - Premium style
  if (data.patientPhoto) {
    try {
      console.log('📥 Loading patient photo:', data.patientPhoto)
      const img = await loadImage(data.patientPhoto)
      console.log('✅ Patient photo loaded')
      const photoSize = 380
      const photoX = (canvas.width - photoSize) / 2
      const photoY = currentY
      
      // Premium soft shadow
      ctx.shadowColor = 'rgba(0, 0, 0, 0.12)'
      ctx.shadowBlur = 40
      ctx.shadowOffsetY = 12
      
      // Outer glow ring
      ctx.fillStyle = '#FECACA'
      ctx.beginPath()
      ctx.arc(photoX + photoSize/2, photoY + photoSize/2, photoSize/2 + 12, 0, Math.PI * 2)
      ctx.fill()
      
      // Red border ring
      ctx.fillStyle = '#DC2626'
      ctx.beginPath()
      ctx.arc(photoX + photoSize/2, photoY + photoSize/2, photoSize/2 + 6, 0, Math.PI * 2)
      ctx.fill()
      
      // White inner ring
      ctx.fillStyle = '#FFFFFF'
      ctx.beginPath()
      ctx.arc(photoX + photoSize/2, photoY + photoSize/2, photoSize/2 + 3, 0, Math.PI * 2)
      ctx.fill()
      
      // Circular photo with smooth edges
      ctx.save()
      ctx.beginPath()
      ctx.arc(photoX + photoSize/2, photoY + photoSize/2, photoSize/2, 0, Math.PI * 2)
      ctx.closePath()
      ctx.clip()
      ctx.drawImage(img, photoX, photoY, photoSize, photoSize)
      ctx.restore()
      
      ctx.shadowBlur = 0
      ctx.shadowOffsetY = 0
      currentY += photoSize + 60
      console.log('✅ Patient photo drawn')
    } catch (error) {
      console.error('⚠️ Error loading patient photo:', error)
    }
  } else {
    // Premium blood drop icon if no photo
    ctx.shadowColor = 'rgba(220, 38, 38, 0.2)'
    ctx.shadowBlur = 30
    ctx.shadowOffsetY = 10
    
    ctx.fillStyle = '#DC2626'
    ctx.beginPath()
    ctx.arc(540, currentY + 140, 110, 0, Math.PI * 2)
    ctx.fill()
    
    ctx.shadowBlur = 0
    ctx.shadowOffsetY = 0
    
    ctx.fillStyle = '#FFFFFF'
    ctx.font = 'bold 140px Arial'
    ctx.textAlign = 'center'
    ctx.fillText('🩸', 540, currentY + 185)
    
    currentY += 320
  }

  // Patient name - Premium typography
  ctx.fillStyle = '#000000'
  ctx.font = 'bold 64px -apple-system, SF Pro Display, Arial'
  ctx.textAlign = 'center'
  ctx.fillText(data.patientName, 540, currentY)
  currentY += 60

  // "PRECISA DE SANGUE" text - refined
  ctx.fillStyle = '#6B7280'
  ctx.font = '400 38px -apple-system, SF Pro Display, Arial'
  ctx.fillText('precisa de sangue', 540, currentY)
  currentY += 100

  // Blood type - Premium card with subtle shadow
  const bloodTypeCardWidth = 220
  const bloodTypeCardHeight = 140
  const bloodTypeX = (canvas.width - bloodTypeCardWidth) / 2
  
  // Premium shadow
  ctx.shadowColor = 'rgba(0, 0, 0, 0.08)'
  ctx.shadowBlur = 25
  ctx.shadowOffsetY = 8
  
  // Change color based on urgency
  const isUrgent = data.urgency === 'critical' || data.urgency === 'high'
  ctx.fillStyle = isUrgent ? '#DC2626' : '#EF4444'
  
  ctx.beginPath()
  ctx.roundRect(bloodTypeX, currentY, bloodTypeCardWidth, bloodTypeCardHeight, 20)
  ctx.fill()
  ctx.shadowBlur = 0
  ctx.shadowOffsetY = 0
  
  ctx.fillStyle = '#FFFFFF'
  ctx.font = 'bold 80px -apple-system, SF Pro Display, Arial'
  ctx.textAlign = 'center'
  ctx.fillText(data.bloodType, 540, currentY + 95)
  currentY += bloodTypeCardHeight + 30
  
  // Urgency badge - refined
  if (isUrgent) {
    ctx.fillStyle = '#DC2626'
    ctx.font = 'bold 28px -apple-system, SF Pro Display, Arial'
    ctx.fillText(data.urgency === 'critical' ? '🚨 URGENTE' : '⚠️ ALTA PRIORIDADE', 540, currentY + 35)
    currentY += 60
  } else {
    currentY += 50
  }

  // Location card - Premium Apple style
  const locationCardPadding = 100
  const locationCardY = currentY
  
  ctx.shadowColor = 'rgba(0, 0, 0, 0.06)'
  ctx.shadowBlur = 20
  ctx.shadowOffsetY = 6
  
  ctx.fillStyle = '#FFFFFF'
  ctx.beginPath()
  ctx.roundRect(locationCardPadding, locationCardY, canvas.width - locationCardPadding * 2, 260, 24)
  ctx.fill()
  ctx.shadowBlur = 0
  ctx.shadowOffsetY = 0
  
  currentY += 50

  // Location icon
  ctx.fillStyle = '#DC2626'
  ctx.font = 'bold 48px Arial'
  ctx.textAlign = 'center'
  ctx.fillText('📍', 540, currentY + 10)
  currentY += 60

  // Hospital name - Premium layout
  ctx.fillStyle = '#000000'
  ctx.font = 'bold 40px -apple-system, SF Pro Display, Arial'
  ctx.textAlign = 'center'
  
  const hospitalText = data.hospital
  if (ctx.measureText(hospitalText).width > 800) {
    ctx.font = 'bold 34px -apple-system, SF Pro Display, Arial'
  }
  ctx.fillText(hospitalText, 540, currentY)
  currentY += 55

  // City and State - Clear and legible
  ctx.fillStyle = '#6B7280'
  ctx.font = '500 36px -apple-system, SF Pro Display, Arial'
  ctx.fillText(`${data.city} · ${data.state}`, 540, currentY)
  currentY += 90

  // Units needed - Premium badge
  const unitsBadgeY = currentY
  ctx.fillStyle = '#FEF2F2'
  ctx.beginPath()
  ctx.roundRect(locationCardPadding + 40, unitsBadgeY, canvas.width - locationCardPadding * 2 - 80, 70, 16)
  ctx.fill()
  
  ctx.fillStyle = '#DC2626'
  ctx.font = 'bold 34px -apple-system, SF Pro Display, Arial'
  const unitsText = `${data.unitsNeeded} ${data.unitsNeeded === 1 ? 'bolsa necessária' : 'bolsas necessárias'}`
  ctx.fillText(unitsText, 540, unitsBadgeY + 48)
  currentY += 110

  // Premium divider
  ctx.strokeStyle = '#F3F4F6'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.moveTo(180, currentY)
  ctx.lineTo(900, currentY)
  ctx.stroke()
  currentY += 70

  // Impact statistics - Apple style
  ctx.fillStyle = '#000000'
  ctx.font = '600 36px -apple-system, SF Pro Display, Arial'
  ctx.textAlign = 'center'
  ctx.fillText('Você Sabia?', 540, currentY)
  currentY += 55

  ctx.fillStyle = '#4B5563'
  ctx.font = '400 30px -apple-system, SF Pro Display, Arial'
  ctx.fillText('No Brasil, 10 mil pessoas precisam', 540, currentY)
  currentY += 42
  ctx.fillText('de transfusão de sangue por dia', 540, currentY)
  currentY += 55

  ctx.fillStyle = '#DC2626'
  ctx.font = 'bold 32px -apple-system, SF Pro Display, Arial'
  ctx.fillText('Uma doação pode salvar até 4 vidas', 540, currentY)
  currentY += 80

  // Premium divider
  ctx.strokeStyle = '#F3F4F6'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.moveTo(180, currentY)
  ctx.lineTo(900, currentY)
  ctx.stroke()
  currentY += 70

  // Call to action - Apple style
  ctx.fillStyle = '#000000'
  ctx.font = 'bold 52px -apple-system, SF Pro Display, Arial'
  ctx.textAlign = 'center'
  ctx.fillText('Ajude Agora', 540, currentY)
  currentY += 55

  ctx.fillStyle = '#6B7280'
  ctx.font = '400 32px -apple-system, SF Pro Display, Arial'
  ctx.fillText('Compartilhe e doe sangue', 540, currentY)
  currentY += 80

  // Premium CTA button - Apple style
  const ctaButtonWidth = 480
  const ctaButtonHeight = 80
  const ctaButtonX = (canvas.width - ctaButtonWidth) / 2
  
  ctx.shadowColor = 'rgba(220, 38, 38, 0.25)'
  ctx.shadowBlur = 20
  ctx.shadowOffsetY = 8
  
  ctx.fillStyle = '#DC2626'
  ctx.beginPath()
  ctx.roundRect(ctaButtonX, currentY, ctaButtonWidth, ctaButtonHeight, 16)
  ctx.fill()
  ctx.shadowBlur = 0
  ctx.shadowOffsetY = 0
  
  ctx.fillStyle = '#FFFFFF'
  ctx.font = 'bold 38px -apple-system, SF Pro Display, Arial'
  ctx.fillText('Baixar App DOE', 540, currentY + 52)

  console.log('🎨 Converting canvas to data URL...')
  
  // Convert to data URL (base64) for portability
  try {
    const dataUrl = canvas.toDataURL('image/png', 1.0)
    console.log('✅ Card generated successfully (data URL length:', dataUrl.length, ')')
    return dataUrl
  } catch (error) {
    console.error('❌ Failed to generate data URL:', error)
    throw new Error('Failed to generate image')
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      console.log('✅ Image loaded:', src)
      resolve(img)
    }
    img.onerror = (error) => {
      console.error('❌ Error loading image:', src, error)
      reject(error)
    }
    img.src = src
  })
}
